// src/client/game/core/Laser.ts

import type { GameModel } from "./GameModel";
import type { GridPos } from "./types";
import type { Snake } from "./Snake";

export interface LaserStrategy {
  fire(model: GameModel, aim?: LaserAim): void;
}

export type LaserAim = { dx: number; dy: number };


interface SnakeHit {
  snakeHitIndex: number; // index into player snake.segments (or -1)
  snakeHitStep: number;  // index into ray where player snake is hit (or -1)
}

/**
 * Build a straight non-wrapping ray from the snake head in its current
 * direction, until we leave the grid.
 *
 * The ray contains only the cells in front of the head (never the head itself).
 */
function buildRay(model: GameModel, aim?: LaserAim): GridPos[] {
  const { snake, width, height } = model;
  const head = snake.head;
  const dir = aim ?? snake.direction;

  const ray: GridPos[] = [];
  let x = head.x;
  let y = head.y;
  const maxSteps = width + height;

  for (let i = 0; i < maxSteps; i++) {
    x += dir.dx;
    y += dir.dy;

    // Non-wrapping: stop when leaving the board
    if (x < 0 || x >= width || y < 0 || y >= height) break;

    ray.push({ x, y });
  }

  return ray;
}

/**
 * Find the first player snake body segment (excluding the head at index 0)
 * hit along the ray, if any.
 */
function findSnakeHit(model: GameModel, ray: GridPos[]): SnakeHit {
  const { snake } = model;

  let snakeHitIndex = -1;
  let snakeHitStep = -1;

  for (let step = 0; step < ray.length; step++) {
    const p = ray[step];
    const idx = snake.segments.findIndex(
      (s, i) => i > 0 && s.x === p.x && s.y === p.y
    );
    if (idx > 0) {
      snakeHitIndex = idx;
      snakeHitStep = step;
      break;
    }
  }

  return { snakeHitIndex, snakeHitStep };
}

/**
 * Find the first obstacle hit along the ray, if any.
 */
function findObstacleHit(model: GameModel, ray: GridPos[]): number {
  const { obstacles } = model;

  for (let step = 0; step < ray.length; step++) {
    const p = ray[step];
    if (obstacles.has(`${p.x},${p.y}`)) {
      return step;
    }
  }

  return -1;
}

/**
 * Compute the step index where the beam stops, given:
 *  - first snake hit step (if any)
 *  - first obstacle hit step (if any)
 *
 * If neither exists, the beam stops at the last cell on the ray (wall).
 */
function computeStopStep(
  rayLength: number,
  snakeHitStep: number,
  obstacleHitStep: number
): number {
  if (rayLength === 0) return -1;

  let stopStep = rayLength - 1; // default: wall (end of ray)

  if (snakeHitStep >= 0 && obstacleHitStep >= 0) {
    stopStep = Math.min(snakeHitStep, obstacleHitStep);
  } else if (snakeHitStep >= 0) {
    stopStep = snakeHitStep;
  } else if (obstacleHitStep >= 0) {
    stopStep = obstacleHitStep;
  }

  return stopStep;
}

/**
 * L1 cut behavior: if we hit our body, cut everything after snakeHitIndex
 * and turn those cut segments into obstacles.
 */
function applyCutTail(model: GameModel, snakeHitIndex: number): void {
  const { snake, obstacles } = model;

  if (snakeHitIndex <= 0 || snake.length <= 2) return;

  const newBody = snake.segments.slice(0, snakeHitIndex + 1);
  const cutTail = snake.segments.slice(snakeHitIndex + 1);

  for (const seg of cutTail) {
    obstacles.add(`${seg.x},${seg.y}`);
  }

  snake.segments = newBody;
}

/**
 * Clear obstacles along the given path.
 */
function clearObstaclesOnPath(model: GameModel, path: GridPos[]): void {
  for (const p of path) {
    model.obstacles.delete(`${p.x},${p.y}`);
  }
}

/**
 * L3 explosion: clear obstacles at the impact cell plus one tile
 * up/down/left/right (if within bounds).
 */
function clearExplosionAtImpact(
  model: GameModel,
  impact: GridPos | undefined
): void {
  if (!impact) return;

  const { width, height, obstacles } = model;

  const positions: GridPos[] = [
    impact, // center
    { x: impact.x + 1, y: impact.y },
    { x: impact.x - 1, y: impact.y },
    { x: impact.x, y: impact.y + 1 },
    { x: impact.x, y: impact.y - 1 },
  ];

  for (const p of positions) {
    if (p.x < 0 || p.x >= width || p.y < 0 || p.y >= height) continue;
    obstacles.delete(`${p.x},${p.y}`);
  }
}

/**
 * Laser vs enemy snakes:
 * - If it hits the enemy head, the whole enemy dies and ALL its segments
 *   turn into obstacles (like when it crashes) and counts as a kill (+100).
 * - If it hits only the body, the part after the hit is cut off and those
 *   cut segments turn into obstacles (just like the main snake), but does
 *   NOT count as a full kill.
 */
function damageEnemySnakesOnPath(model: GameModel, path: GridPos[]): void {
  const enemySnakes = (model.enemySnakes ?? []) as Snake[];
  if (!enemySnakes.length || !path.length) return;

  const survivors: Snake[] = [];

  for (const enemy of enemySnakes) {
    let hitIndex = -1;

    // Find first intersection along the PATH (beam travel order),
    // not just lowest segment index.
    for (let step = 0; step < path.length && hitIndex === -1; step++) {
      const p = path[step];
      const idx = enemy.segments.findIndex(
        (seg) => seg.x === p.x && seg.y === p.y
      );
      if (idx !== -1) {
        hitIndex = idx;
      }
    }

    if (hitIndex === -1) {
      // No hit: enemy unchanged.
      survivors.push(enemy);
      continue;
    }

    if (hitIndex === 0) {
      // Head shot: enemy fully dies, all segments become obstacles.
      for (const seg of enemy.segments) {
        model.obstacles.add(`${seg.x},${seg.y}`);
      }

      // Count this kill (+100 pts via GameModel).
      model.onEnemyKilled();

      // Do NOT push to survivors: it’s dead.
      continue;
    }

    // Tail cut: keep up to hitIndex (inclusive), drop the rest.
    const newBody = enemy.segments.slice(0, hitIndex + 1);
    const cutTail = enemy.segments.slice(hitIndex + 1);

    for (const seg of cutTail) {
      model.obstacles.add(`${seg.x},${seg.y}`);
    }

    enemy.segments = newBody;
    survivors.push(enemy);
  }

  model.enemySnakes = survivors;
}

/**
 * Level 1: classic laser
 * - ignores obstacles entirely
 * - ray goes until wall (or first snake body segment)
 * - cuts your snake if you hit your own body
 * - does NOT interact with enemy snakes
 */
class LaserLevel1Strategy implements LaserStrategy {
  fire(model: GameModel, aim?: LaserAim): void {
    const ray = buildRay(model, aim);
    const { snakeHitIndex, snakeHitStep } = findSnakeHit(model, ray);
    const path = snakeHitStep >= 0 ? ray.slice(0, snakeHitStep + 1) : ray;

    // Visual path
    model.lastLaserPath = path;

    // Cut tail if we actually hit the snake body
    applyCutTail(model, snakeHitIndex);
  }
}

/**
 * Level 2:
 * - beam stops at the first thing it hits:
 *   - player snake body segment
 *   - obstacle
 *   - or the wall (end of ray)
 * - clears obstacles along the path it actually travels (including impact tile)
 * - still cuts your snake if it hits your body
 * - ALSO cuts enemy snakes along that path, turning removed enemy segments
 *   into obstacles and counting headshots as kills.
 */
class LaserLevel2Strategy implements LaserStrategy {
  fire(model: GameModel, aim?: LaserAim): void {
    const ray = buildRay(model, aim);
    const { snakeHitIndex, snakeHitStep } = findSnakeHit(model, ray);
    const obstacleHitStep = findObstacleHit(model, ray);

    const stopStep = computeStopStep(
      ray.length,
      snakeHitStep,
      obstacleHitStep
    );

    if (stopStep < 0) {
      model.lastLaserPath = [];
      return;
    }

    const path = ray.slice(0, stopStep + 1);

    // Visual path up to impact
    model.lastLaserPath = path;

    // Clear obstacles along that path
    clearObstaclesOnPath(model, path);

    // Cut tail only if we actually reached the snake segment
    if (snakeHitIndex > 0 && snakeHitStep >= 0 && snakeHitStep <= stopStep) {
      applyCutTail(model, snakeHitIndex);
    }

    // Damage enemies along the traveled path
    damageEnemySnakesOnPath(model, path);
  }
}

/**
 * Level 3:
 * - same as Level 2 (stops at first hit; clears obstacles along the way)
 * - PLUS a small explosion at the impact point:
 *   - clears obstacles on the impact cell and one tile in each cardinal direction
 * - ALSO damages enemy snakes along the traveled path (same as L2), and
 *   converts removed enemy segments to obstacles. Headshots count as kills.
 */
class LaserLevel3Strategy implements LaserStrategy {
  fire(model: GameModel, aim?: LaserAim): void {
    const ray = buildRay(model, aim);
    const { snakeHitIndex, snakeHitStep } = findSnakeHit(model, ray);
    const obstacleHitStep = findObstacleHit(model, ray);

    const stopStep = computeStopStep(
      ray.length,
      snakeHitStep,
      obstacleHitStep
    );

    if (stopStep < 0) {
      model.lastLaserPath = [];
      return;
    }

    const path = ray.slice(0, stopStep + 1);

    // Visual path up to impact
    model.lastLaserPath = path;

    // Clear obstacles along that path
    clearObstaclesOnPath(model, path);

    // Cut tail only if we actually hit the snake body
    if (snakeHitIndex > 0 && snakeHitStep >= 0 && snakeHitStep <= stopStep) {
      applyCutTail(model, snakeHitIndex);
    }

    // Explosion at the impact point (last cell in the path)
    const impact = path[path.length - 1];
    clearExplosionAtImpact(model, impact);

    // Damage enemies along the traveled path
    damageEnemySnakesOnPath(model, path);
  }
}

const strategies: Record<number, LaserStrategy> = {
  1: new LaserLevel1Strategy(),
  2: new LaserLevel2Strategy(),
  3: new LaserLevel3Strategy(),
};

/**
 * Public entry point: delegate from GameModel.shootLaser to here.
 *
 * Levels:
 * - L1: classic (snake-cut only)
 * - L2: stops at first hit (snake, obstacle, or wall), clears obstacles along path,
 *       and converts removed enemy segments to obstacles (headshots = kills)
 * - L3: same as L2, plus a small cross-shaped explosion around impact
 */
export function shootLaserForGame(model: GameModel, aim?: LaserAim): void {
  // Reset path at the start (for renderer)
  model.lastLaserPath = [];

  const level = model.snake.laserLevel ?? 0;
  if (level <= 0) return;

  const clamped = Math.min(level, 3);
  const strategy = strategies[clamped] ?? strategies[1];

  strategy.fire(model, aim);
}
